// src/context/AuthContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Add axios response interceptor to handle 401 errors
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // If we get a 401, clear the user and redirect to login
          logout();
          navigate('/login');
        }
        return Promise.reject(error);
      }
    );

    // Cleanup the interceptor when the component unmounts
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [navigate]);

  // Check auth state on initial load
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Verify token with backend
          const response = await axios.get('https://ammanstores-qrx8c.ondigitalocean.app/auth/me', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser({ token, ...response.data.data });
        } catch (error) {
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      // const API_URL = 'http://164.92.202.73:3000';
      // const API_URL = 'http://localhost:3000';
      const API_URL = 'https://waslkapi.mahermalhem.site';

      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      
      if (response.data.status && response.data.data) {
        const { access_token: token, user } = response.data.data;
        
        // Check if user is admin and approved
        if (!user.isAdmin) {
          return { 
            success: false, 
            message: 'Access denied. Administrator privileges required.' 
          };
        }
        
        if (!user.isApproved) {
          return { 
            success: false, 
            message: 'Your account is pending approval. Please contact the system administrator.' 
          };
        }
        
        localStorage.setItem('token', token);
        setUser({ token, ...user });
        navigate('/');
        return { success: true };
      } else {
        return { success: false, message: 'Invalid response from server' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed. Please check your credentials.' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/login');
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    loading
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};